import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { User } from '../../types/User';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import UserForm from './UserForm';
import { userTableConfig } from './UserTableConfig';

interface UserFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  role: 'Student' | 'Teacher' | 'Parent' | 'Admin';
  status: 'Active' | 'Inactive';
  approvalStatus: 'Approved' | 'Pending' | 'Rejected';
}

const EnhancedUserManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load users (no need for school filter options in single-tenant)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await apiClient.get('/api/admin/users');
        // Note: No need to update school filter options in single-tenant mode
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };

    loadUsers();
  }, [refreshTrigger]);

  const actions = {
    handleCreate: () => {
      setEditingUser(null);
      setShowForm(true);
    },

    handleEdit: (user: User) => {
      setEditingUser(user);
      setShowForm(true);
    },

    handleView: (user: User) => {
      // For now, view is the same as edit
      // In the future, you could implement a read-only view
      setEditingUser(user);
      setShowForm(true);
    }
  };

  const handleFormSubmit = async (formData: UserFormData) => {
    try {
      setIsLoading(true);

      if (editingUser) {
        console.log('=== USER UPDATE PROCESS START ===');
        console.log('Original user:', editingUser);
        console.log('Form data:', formData);

        // For updates, send user data including active status
        const updateRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          username: formData.username,
          roles: [formData.role.toUpperCase()], // Convert to uppercase enum format
          active: formData.status === 'Active', // Convert Status to active boolean
        };

        console.log('Sending update request:', updateRequest);

        const response = await apiClient.put(`/api/admin/users/${editingUser.id}`, updateRequest);
        console.log('Update response:', response);

        // Handle approval status separately FIRST - this is critical for login
        if (formData.approvalStatus !== editingUser.approvalStatus) {
          console.log(`=== APPROVAL STATUS CHANGE ===`);
          console.log(`Changing approval status from "${editingUser.approvalStatus}" to "${formData.approvalStatus}"`);

          if (formData.approvalStatus === 'Approved') {
            console.log('Calling approve endpoint...');
            const approveResponse = await apiClient.post(`/api/admin/users/${editingUser.id}/approve`);
            console.log('Approve response:', approveResponse);
            console.log('User approved successfully');
          } else if (formData.approvalStatus === 'Rejected') {
            console.log('Calling reject endpoint...');
            const rejectResponse = await apiClient.post(`/api/admin/users/${editingUser.id}/reject`, {
              reason: 'Status changed via admin form'
            });
            console.log('Reject response:', rejectResponse);
            console.log('User rejected successfully');
          }
        } else {
          console.log('No approval status change needed');
        }

        console.log('=== USER UPDATE PROCESS COMPLETE ===');
      } else {
        // For new users, we might need a different endpoint or format
        await apiClient.post('/api/admin/users', formData);
      }

      setShowForm(false);
      setEditingUser(null);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error('Failed to save user:', error);

      // Type-safe error handling
      const errorResponse = error as any; // Type assertion for API errors
      console.error('Error details:', errorResponse?.response?.data); // More detailed error logging

      // Better error handling - show the actual error message
      let errorMessage = 'Failed to save user.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }
      if (errorResponse?.response?.data?.message) {
        errorMessage += ` Server message: ${errorResponse.response.data.message}`;
      }
      if (errorResponse?.response?.data) {
        console.log('Full error response:', errorResponse.response.data);
      }

      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await apiClient.delete(`/api/admin/users/${userId}`);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Manage Users"
          icon={<Users size={32} />}
          subtitle="Add, edit, and manage user accounts and permissions"
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Users size={20} />
            Add New User
          </button>
        </div>

        {/* Admin Table */}
        <AdminTable
          onView={actions.handleView}
          onEdit={actions.handleEdit}
          onDelete={handleDelete}
          config={userTableConfig}
          refreshTrigger={refreshTrigger}
        />

        {/* User Form Modal */}
        <UserForm
          item={editingUser || undefined}
          isOpen={showForm}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default EnhancedUserManagement;
